import intl from 'react-intl-universal';
import { BaseFilterDefinition } from './insightFilterMetadata';

export class InsightPayloadParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParseError';
  }
}

type DrilldownLinks = {
  related: string;
};

type DrilldownEntry = { links: DrilldownLinks };
type DrilldownRoot = { links: DrilldownLinks };
type DrilldownEntries = Record<string, DrilldownEntry>;
export type Drilldown = DrilldownRoot | DrilldownEntries;

export const isDrilldownRoot = (
  value: Drilldown | undefined
): value is DrilldownRoot =>
  !!value && typeof (value as any).links === 'object';

export const isDrilldownEntries = (
  d: Drilldown | undefined
): d is DrilldownEntries => !!d && !isDrilldownRoot(d);

export interface BaseInsightPayload {
  data: {
    type: string;
    id: string;
    attributes: InsightAttributes;
  };
  relationships?: {
    drilldown?: Drilldown;
  };
  meta?: {
    filters: Record<string, BaseFilterDefinition>;
  };
  links: {
    self: string;
  };
}

// Generic insight attributes that can handle various data structures
export interface InsightAttributes {
  data?: InsightDataItem[];
  aggregate?: InsightAggregate;
}

// Generic data item that can represent any insight data structure
export interface InsightDataItem {
  [key: string]: string | number | object | null | undefined;
}

// Generic aggregate structure for metrics with current/prior/percentage_change
export interface InsightAggregate {
  [key: string]: InsightMetric | undefined;
}

export interface InsightMetric {
  current: number | null;
  prior: number | null;
  percentage_change: number | null;
}

export const insightMetricLabelMap = {
  current: 'INSIGHT_METRIC_LABEL.CURRENT',
  prior: 'INSIGHT_METRIC_LABEL.PRIOR',
  percentage_change: 'INSIGHT_METRIC_LABEL.PERCENTAGE_CHANGE',
};

// Specific insight payload types for better type safety when needed
export interface AggregateOnlyInsightPayload extends BaseInsightPayload {
  data: {
    type: string;
    id: string;
    attributes: {
      aggregate: InsightAggregate;
    };
  };
}

export interface DataOnlyInsightPayload extends BaseInsightPayload {
  data: {
    type: string;
    id: string;
    attributes: {
      data: InsightDataItem[];
    };
  };
}

export interface FullInsightPayload extends BaseInsightPayload {
  data: {
    type: string;
    id: string;
    attributes: {
      data: InsightDataItem[];
      aggregate: InsightAggregate;
    };
  };
}

// Utility type to extract the data structure from an insight payload
export type InsightData<T extends BaseInsightPayload> = T extends {
  attributes: { data: infer D };
}
  ? D
  : never;

// Utility type to extract the aggregate structure from an insight payload
export type InsightAggregateData<T extends BaseInsightPayload> = T extends {
  attributes: { aggregate: infer A };
}
  ? A
  : never;

// Type guards for runtime type checking
export const hasData = (
  payload: BaseInsightPayload
): payload is DataOnlyInsightPayload | FullInsightPayload => {
  return (
    'data' in payload.data.attributes &&
    Array.isArray(payload.data.attributes.data)
  );
};

export const hasAggregate = (
  payload: BaseInsightPayload
): payload is AggregateOnlyInsightPayload | FullInsightPayload => {
  return (
    'aggregate' in payload.data.attributes &&
    payload.data.attributes.aggregate !== undefined
  );
};

export const isAggregateOnlyInsightPayload = (
  payload: BaseInsightPayload
): payload is AggregateOnlyInsightPayload => {
  return hasAggregate(payload) && !hasData(payload);
};

export const isFullInsightPayload = (
  payload: BaseInsightPayload
): payload is FullInsightPayload => {
  return hasData(payload) && hasAggregate(payload);
};

// Helper function to safely access insight data
export const getInsightData = <T extends BaseInsightPayload>(
  payload: T
): InsightDataItem[] | undefined => {
  return hasData(payload) ? payload.data.attributes.data : undefined;
};

// Helper function to safely access insight aggregate
export const getInsightAggregate = <T extends BaseInsightPayload>(
  payload: T
): InsightAggregate | undefined => {
  return hasAggregate(payload) ? payload.data.attributes.aggregate : undefined;
};

// Helper function to prepare aggregate data for export
export const flattenAggregateData = (
  aggregate: InsightAggregate
): InsightDataItem[] => {
  const metricName = Object.keys(aggregate || {})[0];
  const valueObject = aggregate?.[metricName];
  return Object.entries(valueObject || {}).map(([key, value]) => ({
    [metricName]: intl.get(
      insightMetricLabelMap[key as keyof typeof insightMetricLabelMap]
    ),
    value: value || 0,
  }));
};

// Helper function to get a specific metric value
export const getMetricValue = (
  aggregate: InsightAggregate,
  metricKey: string,
  valueType: 'current' | 'prior' | 'percentage_change' = 'current'
): number | null => {
  const metric = aggregate[metricKey];
  return metric ? metric[valueType] : null;
};

// Helper function to extract payload data from JSON response
export const extractPayloadData = (jsonResponse: any): BaseInsightPayload => {
  const { data, relationships, links } = jsonResponse;

  if (!data || !data.type || !data.id || !data.attributes) {
    throw new InsightPayloadParseError(
      'Missing required fields: data.type, data.id, or data.attributes'
    );
  }

  if (!links || !links.self) {
    throw new InsightPayloadParseError('Missing required field: links.self');
  }

  return {
    data: {
      type: data.type,
      id: data.id,
      attributes: data.attributes,
    },
    ...(relationships && { relationships }),
    links: {
      self: links.self,
    },
  };
};

// Generic validation function for insight payloads
export const validateInsightStructure = (
  type: string,
  expectedType: string,
  attributes: any,
  requiresData: boolean = false,
  requiresAggregate: boolean = false,
  requiredAggregateFields?: string[]
): void => {
  if (type !== expectedType) {
    throw new InsightPayloadParseError(
      `Expected insight type '${expectedType}' but got '${type}'`
    );
  }

  if (requiresData && !hasDataArray(attributes)) {
    throw new InsightPayloadParseError(
      `Invalid structure: missing or invalid data array for ${expectedType}`
    );
  }

  if (requiresAggregate && !hasAggregateInAttributes(attributes)) {
    throw new InsightPayloadParseError(
      `Invalid structure: missing aggregate for ${expectedType}`
    );
  }

  if (requiredAggregateFields && hasAggregateInAttributes(attributes)) {
    for (const field of requiredAggregateFields) {
      if (!attributes.aggregate[field]) {
        throw new InsightPayloadParseError(
          `Invalid structure: missing required aggregate field '${field}' for ${expectedType}`
        );
      }
    }
  }
};

// Generic function for unknown insight types
export const createGenericInsight = (
  payload: BaseInsightPayload
): BaseInsightPayload => {
  const { data, relationships, links, meta } = payload;

  return {
    data: {
      type: data.type,
      id: data.id,
      attributes: {
        ...(hasDataArray(data.attributes) && { data: data.attributes.data }),
        ...(hasAggregateInAttributes(data.attributes) && {
          aggregate: data.attributes.aggregate,
        }),
      },
    },
    ...(relationships && { relationships }),
    ...(meta && { meta }),
    links,
  };
};

// Helper functions to check for data and aggregate in attributes
export const hasDataArray = (attributes: any): boolean => {
  return 'data' in attributes && Array.isArray(attributes.data);
};

export const hasAggregateInAttributes = (attributes: any): boolean => {
  return 'aggregate' in attributes && attributes.aggregate !== undefined;
};
